// fetchWithRetry — small wrapper around `fetch` that retries on transient
// gateway errors (502/503/504) and network failures.
//
// The Emergent platform briefly returns 502s while pods cold-start; without
// retries every admin button click can flash an "Unexpected response"
// error. With retries the UI stays smooth and only surfaces genuine errors.

const TRANSIENT = new Set([502, 503, 504]);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const fetchWithRetry = async (
  input,
  init = {},
  { attempts = 3, baseDelayMs = 800, factor = 1.6 } = {}
) => {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(input, init);
      if (TRANSIENT.has(res.status) && i < attempts - 1) {
        await sleep(baseDelayMs * Math.pow(factor, i));
        continue;
      }
      return res;
    } catch (err) {
      lastErr = err;
      if (i < attempts - 1) {
        await sleep(baseDelayMs * Math.pow(factor, i));
        continue;
      }
    }
  }
  throw lastErr || new Error("Network error");
};

// Defensive body reader — the Emergent preview-site logger wraps
// `window.fetch` and pre-reads `response.text()` for any non-ok response
// to forward error bodies to its parent frame. That leaves the body
// stream consumed, so a subsequent `await res.text()` in app code throws
// `Failed to execute 'text' on 'Response': body stream already read`.
// This helper swallows that race and just returns an empty string when
// the body is unavailable, so callers can surface a clean HTTP-status
// message instead of leaking the wrapper bug into the UI.
export const safeReadBody = async (res) => {
  if (!res) return "";
  try { return await res.text(); } catch { return ""; }
};

// JSON convenience helper — returns parsed body or throws with a useful
// message including HTTP status and the first 200 chars of the body.
export const fetchJson = async (input, init, opts) => {
  const res = await fetchWithRetry(input, init, opts);
  if (!res.ok) {
    const body = await safeReadBody(res);
    const err = new Error(`HTTP ${res.status} ${res.statusText || ""}${body ? " · " + body.slice(0, 200) : ""}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};
